export const COOKIE_CONSENT_STORAGE_KEY = 'cookie-consent:v1';

export interface CookieConsentChoice {
    analytics: boolean;
}

export interface ConsentStorage {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
    removeItem: (key: string) => void;
}

export interface CookieLocation {
    hostname: string;
    protocol: string;
}

const ANALYTICS_KEY_PREFIXES = ['ph_', '__ph_'] as const;
// Google Analytics cookies: `_ga`, `_ga_<stream id>`, `_gid`, `_gat`, `_gat_<suffix>`.
// Matched exactly so look-alikes such as `_gameSettings` survive.
const GA_COOKIE_NAME = /^(_ga(_[A-Z0-9]+)?|_gid|_gat(_\w+)?)$/;

function isCookieConsentChoice(value: unknown): value is CookieConsentChoice {
    if (typeof value !== 'object' || value === null) return false;
    return 'analytics' in value && typeof value.analytics === 'boolean';
}

export function isAnalyticsStorageKey(key: string) {
    return ANALYTICS_KEY_PREFIXES.some((prefix) => key.startsWith(prefix)) || GA_COOKIE_NAME.test(key);
}

export function readCookieConsent(storage: ConsentStorage): CookieConsentChoice | null {
    try {
        const raw = storage.getItem(COOKIE_CONSENT_STORAGE_KEY);
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        if (!isCookieConsentChoice(parsed)) return null;
        return { analytics: parsed.analytics };
    } catch {
        return null;
    }
}

export function writeCookieConsent(choice: CookieConsentChoice, storage: ConsentStorage) {
    try {
        storage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(choice));
    } catch {
        // Private mode, disabled storage, or a full quota.
    }
}

export function clearAnalyticsStorageKeys(keys: readonly string[], remove: (key: string) => void) {
    for (const key of keys) {
        if (isAnalyticsStorageKey(key)) remove(key);
    }
}

/** Parent domain PostHog and GA use for `Domain=.promote.fun`. Host-only names and IPs stay host-only. */
export function analyticsCookieParentDomain(hostname: string) {
    if (hostname === 'localhost' || hostname === '127.0.0.1') return null;
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return null;
    const parts = hostname.split('.').filter((part) => part.length > 0);
    if (parts.length < 2) return null;
    return parts.slice(-2).join('.');
}

/** Expiry strings that match a host-only cookie and a `Domain` + `Secure` cookie. */
export function analyticsCookieExpiryValues(name: string, location: CookieLocation) {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    const values = [`${name}=; Max-Age=0; path=/${secure}`];
    const parent = analyticsCookieParentDomain(location.hostname);
    if (parent) {
        values.push(`${name}=; Max-Age=0; path=/; domain=.${parent}${secure}`);
    }
    return values;
}

function browserStorage(storage: Storage | undefined): ConsentStorage | null {
    if (!storage) return null;
    return storage;
}

export function readStoredCookieConsent() {
    const storage = browserStorage(typeof localStorage === 'undefined' ? undefined : localStorage);
    if (!storage) return null;
    return readCookieConsent(storage);
}

export function writeStoredCookieConsent(choice: CookieConsentChoice) {
    const storage = browserStorage(typeof localStorage === 'undefined' ? undefined : localStorage);
    if (!storage) return;
    writeCookieConsent(choice, storage);
}

function clearNamedStorage(storage: Storage | undefined) {
    if (!storage) return;
    clearAnalyticsStorageKeys(Object.keys(storage), (key) => storage.removeItem(key));
}

export function clearAnalyticsBrowserStorage() {
    try {
        clearNamedStorage(typeof localStorage === 'undefined' ? undefined : localStorage);
    } catch {
        // Storage unavailable.
    }

    try {
        clearNamedStorage(typeof sessionStorage === 'undefined' ? undefined : sessionStorage);
    } catch {
        // Storage unavailable.
    }

    try {
        if (typeof document === 'undefined') return;
        const names = document.cookie
            .split('; ')
            .map((cookie) => cookie.trim().split('=')[0] ?? '')
            .filter((name) => name.length > 0 && isAnalyticsStorageKey(name));
        const location = typeof window === 'undefined' ? { hostname: '', protocol: 'https:' } : window.location;
        for (const name of names) {
            for (const value of analyticsCookieExpiryValues(name, location)) {
                // Cookie Store is still missing in Firefox. Only PostHog and GA names reach this write.
                // biome-ignore lint/suspicious/noDocumentCookie: expire analytics cookies in every browser
                document.cookie = value;
            }
        }
    } catch {
        // Cookies unavailable.
    }
}
